/**
 * Shopify Customer Module
 * Handles fetching customer data including profile, addresses, orders, and metafields
 */

class ShopifyCustomer {
  constructor(apiClient) {
    this.api = apiClient;
    this.utils = window.ShopifyUtils;
  }

  /**
   * Customer query to fetch all customer data
   */
  CUSTOMER_QUERY = `
    query getCustomer($customerAccessToken: String!) {
      customer(customerAccessToken: $customerAccessToken) {
        id
        firstName
        lastName
        email
        phone
        acceptsMarketing
        createdAt
        updatedAt
        numberOfOrders
        defaultAddress {
          id
          firstName
          lastName
          company
          address1
          address2
          city
          province
          provinceCode
          country
          countryCodeV2
          zip
          phone
        }
        addresses(first: 20) {
          edges {
            node {
              id
              firstName
              lastName
              company
              address1
              address2
              city
              province
              provinceCode
              country
              countryCodeV2
              zip
              phone
            }
          }
        }
        orders(first: 50) {
          edges {
            node {
              id
              orderNumber
              name
              processedAt
              financialStatus
              fulfillmentStatus
              totalPrice {
                amount
                currencyCode
              }
              subtotalPrice {
                amount
                currencyCode
              }
              totalShippingPrice {
                amount
                currencyCode
              }
              totalTax {
                amount
                currencyCode
              }
              lineItems(first: 50) {
                edges {
                  node {
                    title
                    quantity
                    variant {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      image {
                        url
                        altText
                      }
                      product {
                        id
                        title
                        handle
                      }
                    }
                    customAttributes {
                      key
                      value
                    }
                  }
                }
              }
              shippingAddress {
                firstName
                lastName
                address1
                address2
                city
                province
                country
                zip
              }
              statusUrl
            }
          }
        }
        metafields(identifiers: [
          { namespace: "custom", key: "collar" },
          { namespace: "custom", key: "chest" },
          { namespace: "custom", key: "fit" },
          { namespace: "custom", key: "sleeve" },
          { namespace: "custom", key: "waist" },
          { namespace: "custom", key: "collar_style" },
          { namespace: "custom", key: "cuff_style" },
          { namespace: "custom", key: "pocket_style" },
          { namespace: "custom", key: "monogram" },
          { namespace: "custom", key: "monogram_initials" },
          { namespace: "custom", key: "monogram_text" },
          { namespace: "custom", key: "monogram_font" },
          { namespace: "custom", key: "monogram_color" },
          { namespace: "custom", key: "monogram_placement" },
          { namespace: "custom", key: "tail_style" }
        ]) {
          key
          namespace
          value
          type
        }
      }
    }
  `;

  /**
   * Fetch customer data by access token
   * @param {string} customerAccessToken - Customer access token
   * @returns {Promise<Object>}
   */
  async getCustomer(customerAccessToken) {
    try {
      if (!customerAccessToken) {
        throw new Error('Customer access token is required');
      }

      const variables = {
        customerAccessToken: customerAccessToken
      };

      const response = await this.api.query(this.CUSTOMER_QUERY, variables, customerAccessToken);

      if (response.errors && response.errors.length > 0) {
        console.error('GraphQL errors:', response.errors);
        throw new Error(response.errors[0].message);
      }

      const customer = response.data?.customer;

      if (!customer) {
        throw new Error('Customer not found or token expired');
      }

      // Process and normalize customer data
      const processedCustomer = this.processCustomerData(customer);

      return {
        success: true,
        customer: processedCustomer
      };
    } catch (error) {
      console.error('Error fetching customer:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Process and normalize customer data for easier consumption
   * @param {Object} customer - Raw customer data from GraphQL
   * @returns {Object}
   */
  processCustomerData(customer) {
    // Get the default address ID for comparison
    const defaultAddressId = customer.defaultAddress?.id;
    
    // Process addresses from edges format and mark default
    const addresses = customer.addresses?.edges?.map(edge => {
      const address = edge.node;
      return {
        ...address,
        isDefault: defaultAddressId ? address.id === defaultAddressId : false
      };
    }) || [];
    
    // Process orders from edges format
    const orders = customer.orders?.edges?.map(edge => {
      const order = edge.node;
      return {
        ...order,
        lineItems: order.lineItems?.edges?.map(itemEdge => itemEdge.node) || []
      };
    }) || [];

    // Process metafields into an easier-to-use object
    const metafields = {};
    if (customer.metafields) {
      customer.metafields.forEach(metafield => {
        if (metafield && metafield.key) {
          metafields[metafield.key] = metafield.value;
        }
      });
    }

    return {
      id: customer.id,
      firstName: customer.firstName,
      lastName: customer.lastName,
      email: customer.email,
      phone: customer.phone,
      acceptsMarketing: customer.acceptsMarketing,
      createdAt: customer.createdAt,
      updatedAt: customer.updatedAt,
      numberOfOrders: customer.numberOfOrders,
      defaultAddress: customer.defaultAddress,
      addresses: addresses,
      orders: orders,
      metafields: metafields
    };
  }

  /**
   * Update customer information
   * @param {string} customerAccessToken - Customer access token
   * @param {Object} customerData - Data to update
   * @returns {Promise<Object>}
   */
  async updateCustomer(customerAccessToken, customerData) {
    const CUSTOMER_UPDATE_MUTATION = `
      mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
        customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
          customer {
            id
            firstName
            lastName
            email
            phone
            acceptsMarketing
          }
          customerUserErrors {
            code
            field
            message
          }
        }
      }
    `;

    try {
      const variables = {
        customerAccessToken: customerAccessToken,
        customer: customerData
      };

      const response = await this.api.mutate(CUSTOMER_UPDATE_MUTATION, variables);

      if (response.data?.customerUpdate?.customerUserErrors?.length > 0) {
        const error = response.data.customerUpdate.customerUserErrors[0];
        throw new Error(error.message);
      }

      return {
        success: true,
        customer: response.data?.customerUpdate?.customer
      };
    } catch (error) {
      console.error('Error updating customer:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
}

// Export for use in other modules
if (typeof window !== 'undefined') {
  window.ShopifyCustomer = ShopifyCustomer;
}
